import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { profileSelector } from 'selectors'
import { updateProfile } from 'actions/profile'
import styled from 'styled-components/macro'
import Page from 'rui/widgets/Page'
import Thin from 'components/layout/Thin'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Form from 'components/form'
import Input from 'components/form/Input'
import { ssn, minLength, maxLength } from 'components/form/validations'
import Step from 'components/misc/Step'
import Icon from 'components/misc/Icon'
import Spacer from 'components/misc/Spacer'
import { body2 } from 'styles/mixins/textstyles'
import { ExternalLink } from 'components/layout/links'
import { formatRegistrationStep } from 'util/registrationFlow'
import { LocalizedRichText, LocalizedText, withLocalization } from 'localization'

const BackgroundAuth = styled.div`
  width: 100%;
  height: 20rem;
  margin: 2.4rem 0 0;
  padding: 1.2rem 1.6rem;
  border: 1px solid ${(props) => props.theme.colors.greyDarker};
  color: ${(props) => props.theme.colors.greyDarker};
  border-radius: 0.5rem;
  overflow: scroll;
  text-align: left;
`

const IconList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: space-between;
  padding-top: 0.8rem;

  li {
    margin: 0;
    list-style: none;
    font-size: 1.1rem;
    font-weight: 700;
    position: relative;
    color: ${(props) => props.theme.colors.greyDark};
    text-transform: uppercase;
    display: flex;
    align-items: center;
  }
`

const StyledIcon = styled(Icon).attrs(() => ({ palette: 'grey', shade: 'light' }))`
  width: 1.4rem;
  height: 1.4rem;
  margin-right: 0.5rem;
`

const WithPara = styled.div`
  display: flex;
  align-items: center;

  p {
    ${(props) => body2(props.theme.colors.black, 'medium')}
  }

  & > :first-child {
    width: 8rem;
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 2.4rem;
  }

  & > :last-child {
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: ${(props) => props.theme.colors.greyDark};
    text-align: left;
    display: inline-block;
    flex-shrink: 1;
  }
`

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 5rem;
`

const InitialsField = styled(Input.Initials)`
  text-transform: uppercase;

  &::placeholder {
    text-transform: none;
  }
`

const Background = ({ dispatch, profile, localization }) => {
  const { t } = localization

  const handleData = (data) => {
    return dispatch(updateProfile({ profile: data }))
  }

  const registrationStep = formatRegistrationStep('BACKGROUND', profile)

  return (
    <Page>
      <Helmet>
        <title>{t('dweb_background_consumer_report')}</title>
      </Helmet>
      <Thin>
        <Form onData={handleData}>
          <Panel>
            <PanelHeading>
              <LocalizedText translationKey="dweb_background_consumer_report" />
            </PanelHeading>
            <PanelSubheading>
              <LocalizedText translationKey="dweb_background_safety_our_community_roadie_orders_consumer_reports" />
            </PanelSubheading>

            <Input.Integer
              password
              maxLength={9}
              className="fs-block"
              type="tel"
              label={t('dweb_background_social_security_number')}
              name="ssn"
              validate={ssn.required()}
            />

            <IconList>
              <li>
                <StyledIcon symbol="lock" /> <LocalizedText translationKey="dweb_common_secure_transfer" />
              </li>
              <li>
                <StyledIcon symbol="shield" /> <LocalizedText translationKey="dweb_common_never_shared" />
              </li>
              <li>
                <StyledIcon symbol="ban" /> <LocalizedText translationKey="dweb_common_no_credit_checks" />
              </li>
            </IconList>

            <BackgroundAuth>
              <p>
                <LocalizedRichText
                  translationKey="dweb_background_consumer_report_authorization_acknowledgement"
                  params={{
                    strongText1: (
                      <strong>
                        <u>
                          <LocalizedText translationKey="dweb_background_check_disclosure_authorization" />
                        </u>
                      </strong>
                    ),
                    br: <br />,
                    link1: (
                      <ExternalLink href="https://www.roadie.com/app/disclosure">
                        <LocalizedText translationKey="dweb_background_www_roadie_com_app_disclosure" />
                      </ExternalLink>
                    ),
                  }}
                />
              </p>
              <br />
              <p>
                <LocalizedRichText
                  translationKey="dweb_background_washington_motor_vehicle_record_authorization"
                  params={{
                    strongText1: (
                      <strong>
                        <u>
                          <LocalizedText translationKey="dweb_background_washington_candidates_only" />
                        </u>
                      </strong>
                    ),
                    br: <br />,
                    strongText2: (
                      <strong>
                        <LocalizedText translationKey="dweb_background_motor_vehicle_record_disclosure_authorization" />
                      </strong>
                    ),
                  }}
                />
              </p>
              <br />
              <p>
                <LocalizedText translationKey="dweb_background_i_agree_electronic_copy_authorization_shall_as_valid" />
              </p>
            </BackgroundAuth>

            <Spacer height="1.6rem" />

            <WithPara>
              <Centered>
                <Input.Checkbox id="consumer_report" name="consumer-report" />
              </Centered>
              <p>
                <label htmlFor="consumer_report">
                  <LocalizedRichText
                    translationKey="dweb_background_consumer_report_copy_checkbox_label"
                    params={{
                      strongText1: (
                        <strong>
                          <LocalizedText translationKey="dweb_background_residents_ca_me_md_ma_nj_ok" />
                        </strong>
                      ),
                      br: <br />,
                    }}
                  />
                </label>
              </p>
            </WithPara>

            <Spacer height="1.6rem" />

            <WithPara>
              <InitialsField
                label={t('dweb_background_initials')}
                id="initials"
                name="initials"
                validate={minLength(2).and(maxLength(2)).required()}
              />
              <p>
                <label htmlFor="initials">
                  <LocalizedText translationKey="dweb_background_by_initializing_box_i_acknowledge_i_have_read" />
                </label>
              </p>
            </WithPara>

            <Input.Submit value={t('dweb_common_next')} />

            {registrationStep && <Step>{registrationStep}</Step>}
          </Panel>
        </Form>
      </Thin>
    </Page>
  )
}

export default connect(profileSelector)(withLocalization(Background))
